import React from "react";
import TableCell from "../../../components/SmartTable/TableCell";
import DeleteItemButton from "../../../components/SmartTable/TableFiltersActions/DeleteItemButton";
import { FILTER_TYPE_STATIC_SELECT, FILTER_TYPE_STRING } from "../../../components/SmartTable/methods";
import { formatString, getActiveFilterOptions } from "../../../utils/common";
import { openModalAction } from "../../../components/Modal/reducer";
import ColumnActiveSwitch from "../../Organization/OrgSettings/Functions/ColumnActiveSwitch";
import { EditButton } from "../../../components/EditButton";
import Tag from "../../../components/Tag";
import Tooltip from "../../../components/Tooltip";
import { expiryDateTransformer } from "./trainingValidityHelpers";
import { MODAL_SHARE_TRAINING } from "./ShareTrainingForm";
import { TABLE_TRG_TRAININGS, MODAL_TRG_TRAINING } from "./trainingConstants";
import { downloadTrgSingleTraining } from "./trainingExport";

export { downloadTrgSingleTraining } from "./trainingExport";

/**
 * Generates the SmartTable column configuration for the TRG Trainings list.
 * Includes: Shared status (multi-company only), Code, Name, Validity, Provider,
 * Active status, and Actions (manage permission only).
 *
 * @param {boolean} isCompanyLinked - Whether the company is linked to another tenant.
 * @param {boolean} canManage - Whether the logged-in user may modify trainings.
 * @param {Function} trans - i18next translation function.
 * @param {Function} dispatch - Redux dispatch function.
 * @param {Function} format - App date formatter used by expiryDateTransformer.
 * @returns {Array<object>} Ant Design Table column descriptors.
 */
export const generateColumns = (isCompanyLinked, canManage, trans, dispatch, format) => {
  return [
    ...(isCompanyLinked
      ? [
          {
            title: "trg.shared",
            render: (row) => {
              return (
                <TableCell minWidth={150} maxWidth={210}>
                  {!!row.shared_by_company_name && (
                    <Tag className="tag-primary">
                      {trans("qam.shared_by")} {row.shared_by_company_name}
                    </Tag>
                  )}
                  {!!row.is_shared && <Tag className="tag-secondary">{trans("qam.shared_with_other_companies")}</Tag>}
                  {!row.is_shared && !row.shared_by_company_name && <Tag>{trans("trg.not_shared")}</Tag>}
                </TableCell>
              );
            }
          }
        ]
      : []),
    {
      title: "trg.trainings.code",
      dataIndex: "code",
      sorter: true,
      filterType: FILTER_TYPE_STRING,
      render: (text, row) => <TableCell minWidth={100}>{formatString(row.code, "-")}</TableCell>
    },
    {
      title: "trg.trainings.name",
      dataIndex: "name",
      sorter: true,
      filterType: FILTER_TYPE_STRING,
      render: (text, row) => <TableCell minWidth={150}>{formatString(row.name, "-")}</TableCell>
    },
    {
      title: "trg.trainings.validity",
      render: (row) => {
        const { dataForExpiryDate, expiryDateText } = expiryDateTransformer(row, format);
        return (
          <TableCell minWidth={150}>
            {dataForExpiryDate && `${dataForExpiryDate} `}
            {expiryDateText && trans(expiryDateText, { years: row.validity.year_number })}
          </TableCell>
        );
      }
    },
    {
      title: "trg.new_training.provider",
      dataIndex: "provider_name",
      sorter: true,
      filterType: FILTER_TYPE_STRING,
      render: (text, row) => {
        const formattedProvider = row.provider_name || trans("trg.new_training.internal");
        return <TableCell minWidth={150}>{formattedProvider}</TableCell>;
      }
    },
    {
      title: "trg.external_providers.active",
      dataIndex: "active",
      sorter: true,
      filterType: FILTER_TYPE_STATIC_SELECT,
      filterProps: {
        options: getActiveFilterOptions(trans)
      },
      render: (text, row) => (
        <TableCell width={100}>
          {trans(row.active ? "container.general_words.active" : "container.general_words.inactive")}
        </TableCell>
      )
    },
    ...(canManage
      ? [
          {
            title: trans("container.general_words.actions"),
            fixed: "right",
            width: 140,
            onCell: () => ({
              style: { right: "-9px" }
            }),
            render: (row = {}) => (
              <div className="table-actions-column">
                <Tooltip title={trans("container.general_words.export")} placement="top" className="text-muted">
                  <button
                    type="button"
                    className="text-muted"
                    onClick={() => {
                      downloadTrgSingleTraining(trans, dispatch, row, "trg/training/export");
                    }}
                    style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                  >
                    <i className="ti-download" />
                  </button>
                </Tooltip>
                <ColumnActiveSwitch
                  disabled={!row.can_be_activated}
                  className="mr-10"
                  isActive={row.active}
                  route={`trg/trainings/change-state/${row.id}`}
                  tableName={TABLE_TRG_TRAININGS}
                  tooltip={{
                    active: row.can_be_activated
                      ? "trg.trainings.deactivate_training_tooltip"
                      : "trg.training_cannot_be_activated",
                    inactive: row.can_be_activated
                      ? "trg.trainings.activate_training_tooltip"
                      : "trg.training_cannot_be_activated"
                  }}
                />
                {isCompanyLinked && (
                  <Tooltip title={trans("trg.share")}>
                    <button
                      type="button"
                      className="text-primary mr-15"
                      disabled={!row.active || !!row.shared_by_company_name}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!row.active || !!row.shared_by_company_name) return;
                        dispatch(openModalAction({ name: MODAL_SHARE_TRAINING, data: row }));
                      }}
                      style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                    >
                      <i className="ti-share" />
                    </button>
                  </Tooltip>
                )}
                <EditButton
                  onClick={() => {
                    dispatch(openModalAction({ name: MODAL_TRG_TRAINING, data: row }));
                  }}
                />
                <DeleteItemButton
                  disabled={!row.canBeDeleted || row.is_shared || !!row.shared_by_company_name}
                  tableName={TABLE_TRG_TRAININGS}
                  route={`trg/trainings/${row.id}`}
                  content={
                    <div>
                      <div>{trans("container.confirm_modal.form_subtitle")}</div>
                    </div>
                  }
                />
              </div>
            )
          }
        ]
      : [])
  ];
};
